import type { ComponentProps, ReactNode } from 'react';
import {
  ActivityIndicator,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Card } from './Card';
import { useColors } from '../lib/colors';
import { useIsDesktop } from '../hooks/useIsDesktop';

type FloatingActionBannerTone = 'neutral' | 'success' | 'warning' | 'error';
type FloatingActionBannerButtonVariant = 'primary' | 'secondary';

type FloatingActionBannerProps = {
  title: ReactNode;
  message?: ReactNode;
  iconName?: ComponentProps<typeof Ionicons>['name'];
  iconElement?: ReactNode;
  iconBackgroundClassName?: string;
  iconColor?: string;
  actions?: ReactNode;
  tone?: FloatingActionBannerTone;
  desktopMaxWidth?: number;
  desktopMinWidth?: number;
  desktopTopOffset?: number;
  mobileTopOffset?: number;
  desktopHorizontalInset?: number;
  mobileHorizontalInset?: number;
};

type FloatingActionBannerButtonProps = {
  children: ReactNode;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: FloatingActionBannerButtonVariant;
};

const TONE_CLASSES: Record<
  FloatingActionBannerTone,
  {
    card: string;
    icon: string;
    title: string;
    message: string;
  }
> = {
  neutral: {
    card: 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900',
    icon: 'bg-gray-600',
    title: 'text-gray-950 dark:text-gray-50',
    message: 'text-gray-600 dark:text-gray-300',
  },
  success: {
    card: 'border-green-200 bg-white dark:border-green-800 dark:bg-gray-900',
    icon: 'bg-forest-600',
    title: 'text-gray-950 dark:text-gray-50',
    message: 'text-gray-600 dark:text-gray-300',
  },
  warning: {
    card: 'border-amber-200 bg-white dark:border-amber-800 dark:bg-gray-900',
    icon: 'bg-amber-600',
    title: 'text-gray-950 dark:text-gray-50',
    message: 'text-gray-600 dark:text-gray-300',
  },
  error: {
    card: 'border-red-200 bg-white dark:border-red-800 dark:bg-gray-900',
    icon: 'bg-red-600',
    title: 'text-gray-950 dark:text-gray-50',
    message: 'text-gray-600 dark:text-gray-300',
  },
};

export function FloatingActionBanner({
  title,
  message,
  iconName,
  iconElement,
  iconBackgroundClassName,
  iconColor = '#ffffff',
  actions,
  tone = 'neutral',
  desktopMaxWidth = 520,
  desktopMinWidth = 440,
  desktopTopOffset = 20,
  mobileTopOffset = 12,
  desktopHorizontalInset = 32,
  mobileHorizontalInset = 16,
}: FloatingActionBannerProps) {
  const isDesktop = useIsDesktop();
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const theme = TONE_CLASSES[tone];
  const horizontalInset = isDesktop
    ? desktopHorizontalInset
    : mobileHorizontalInset;
  const cardWidth = Math.min(
    desktopMaxWidth,
    Math.max(desktopMinWidth, windowWidth - horizontalInset * 2),
  );

  return (
    <View
      pointerEvents="box-none"
      style={{
        position: 'absolute',
        top: isDesktop ? desktopTopOffset : insets.top + mobileTopOffset,
        left: horizontalInset,
        right: horizontalInset,
        zIndex: 999,
      }}
    >
      <Card
        className={`rounded-xl px-5 py-4 shadow-card-lg ${theme.card}`}
        style={{
          alignSelf: isDesktop ? 'flex-end' : 'stretch',
          ...(isDesktop ? { width: cardWidth } : {}),
        }}
      >
        <View className="flex-row items-start gap-3">
          {iconElement || iconName ? (
            <View
              className={`mt-0.5 h-6 w-6 shrink-0 items-center justify-center rounded-md ${
                iconBackgroundClassName ?? theme.icon
              }`}
            >
              {iconElement ?? (
                <Ionicons name={iconName} size={16} color={iconColor} />
              )}
            </View>
          ) : null}
          <View className="min-w-0 flex-1">
            <Text className={`text-base font-semibold ${theme.title}`}>
              {title}
            </Text>
            {message ? (
              <Text className={`mt-1 text-base leading-6 ${theme.message}`}>
                {message}
              </Text>
            ) : null}
          </View>
        </View>
        {actions ? (
          <View className="mt-5 flex-row flex-wrap justify-end gap-3">
            {actions}
          </View>
        ) : null}
      </Card>
    </View>
  );
}

export function FloatingActionBannerButton({
  children,
  onPress,
  disabled,
  loading,
  variant = 'secondary',
}: FloatingActionBannerButtonProps) {
  const c = useColors();
  const isDisabled = disabled || loading;
  const containerClassName =
    variant === 'primary'
      ? 'bg-forest-600 active:bg-forest-700'
      : 'border border-gray-300 bg-white active:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:active:bg-gray-800';
  const textClassName =
    variant === 'primary' ? 'text-white' : 'text-gray-900 dark:text-gray-100';
  const spinnerColor = variant === 'primary' ? '#ffffff' : c.primary;

  return (
    <Pressable
      onPress={onPress}
      disabled={isDisabled}
      className={`flex-row items-center justify-center rounded-xl px-4 py-2 ${
        isDisabled ? 'opacity-50' : ''
      } ${containerClassName}`}
    >
      {loading ? (
        <ActivityIndicator size="small" color={spinnerColor} className="mr-2" />
      ) : null}
      <Text className={`text-sm font-semibold ${textClassName}`}>
        {children}
      </Text>
    </Pressable>
  );
}
